import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Modal } from 'react-bootstrap';
import { BsPlusCircleFill, BsPencilSquare, BsTrash, BsCheckLg, BsX } from 'react-icons/bs';
import { FaList } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import DepartmentSidebar from './components/DepertmentSidebar';
import DepartmentTopbar from './components/DepartmentTopbar';

function CashSubCatagory() {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [subCategoryName, setSubCategoryName] = useState('');
  const [subCategories, setSubCategories] = useState([]);
  const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subCategoryToDelete, setSubCategoryToDelete] = useState(null);
  
  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem('token');

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}cash_catagory_list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        toast.error(`Error fetching categories: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategories
  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}cash_subcatagory_list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSubCategories(response.data.data);
      } else {
        toast.error(`Error fetching subcategories: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Failed to fetch subcategories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, []);

  const resetForm = () => {
    setSelectedCategoryId('');
    setSubCategoryName('');
    setEditingSubCategoryId(null);
  };

  const handleCreateSubCategory = async (event) => {
    event.preventDefault();
    const trimmedName = subCategoryName.trim();
    if (!selectedCategoryId || !trimmedName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}add_cash_subcatagory.php`,
        {
          catagory_id: selectedCategoryId,
          subcatagory_name: trimmedName
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        const newSubCategory = {
          id: response.data.id || Date.now(),
          catagory_id: selectedCategoryId,
          subcatagory_name: trimmedName,
          catagory_name: categories.find(cat => cat.id == selectedCategoryId)?.catagory_name || ''
        };
        setSubCategories(prev => [...prev, newSubCategory]);
        resetForm();
      } else {
        toast.error(response.data.message || 'Failed to add subcategory');
      }
    } catch (error) {
      console.error('Error adding subcategory:', error);
      toast.error('An error occurred while adding the subcategory');
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirmation = (id) => {
    setSubCategoryToDelete(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!subCategoryToDelete) return;
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}delete_cash_subcatagory.php`,
        { id: subCategoryToDelete },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchSubCategories();
      } else {
        toast.error(response.data.message || 'Failed to delete subcategory');
      }
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      toast.error('An error occurred while deleting the subcategory');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setSubCategoryToDelete(null);
    }
    
    if (editingSubCategoryId === subCategoryToDelete) {
      cancelEditSubCategory();
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSubCategoryToDelete(null);
  };
  const handleUpdateSubCategory = async (event) => {
    event.preventDefault();
    const trimmedName = subCategoryName.trim();
    if (!selectedCategoryId || !trimmedName || !editingSubCategoryId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const selectedCategory = categories.find(cat => cat.id == selectedCategoryId);
      
      const response = await axios.post(
        `${API_URL}update_cash_subcatagory.php`,
        {
          id: editingSubCategoryId,
          catagory_id: selectedCategoryId,
          catagory_name: selectedCategory?.catagory_name || '',
          subcatagory_name: trimmedName
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setSubCategories(prev => prev.map(item => 
          item.id === editingSubCategoryId 
            ? { 
                ...item, 
                catagory_id: selectedCategoryId,
                subcatagory_name: trimmedName,
                catagory_name: selectedCategory?.catagory_name || ''
              }
            : item
        ));
        resetForm();
      } else {
        toast.error(response.data.message || 'Failed to update subcategory');
      }
    } catch (error) {
      console.error('Error updating subcategory:', error);
      toast.error('An error occurred while updating the subcategory');
    } finally {
      setLoading(false);
    }
  };

  const startEditSubCategory = (subCategory) => {
    setEditingSubCategoryId(subCategory.id);
    setSelectedCategoryId(subCategory.catagory_id);
    setSubCategoryName(subCategory.subcatagory_name);
  };

  const cancelEditSubCategory = () => {
    setEditingSubCategoryId(null);
    resetForm();
  };

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <Container fluid className="p-4 bg-light">
          <header className="mb-4">
            <h1 className="h3">Cash Subcategories</h1>
          </header>
          <Row className="g-4">
            <Col lg={5}>
              <Card className="shadow-sm h-100">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center gap-2"
                  style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}
                >
                  {editingSubCategoryId ? (
                    <BsPencilSquare style={{ color: '#1976d2' }} />
                  ) : (
                    <BsPlusCircleFill style={{ color: '#1976d2' }} />
                  )}
                  <span style={{ color: '#1976d2' }}>
                    {editingSubCategoryId ? 'Update' : 'Create New'} Subcategory
                  </span>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={editingSubCategoryId ? handleUpdateSubCategory : handleCreateSubCategory}>
                    <Form.Group controlId="categorySelect" className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Select
                        value={selectedCategoryId}
                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.catagory_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="subCategoryName" className="mb-3">
                      <Form.Label>Subcategory Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter subcategory name"
                        value={subCategoryName}
                        onChange={(e) => setSubCategoryName(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button 
                        variant={editingSubCategoryId ? "success" : "primary"} 
                        type="submit" 
                        disabled={loading}
                        className="flex-grow-1"
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" />
                        ) : editingSubCategoryId ? (
                          <>
                            <BsCheckLg className="me-1" /> Update
                          </>
                        ) : (
                          <>
                            <BsPlusCircleFill className="me-1" /> Create
                          </>
                        )}
                      </Button>
                      {editingSubCategoryId && (
                        <Button 
                          variant="secondary" 
                          onClick={cancelEditSubCategory}
                          disabled={loading}
                        >
                          <BsX />
                        </Button>
                      )}
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center gap-2"
                  style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}
                >
                  <FaList style={{ color: '#1976d2' }} />
                  <span style={{ color: '#1976d2' }}>Subcategories List</span>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Category</th>
                          <th>Subcategory</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && subCategories.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center">
                              <Spinner animation="border" size="sm" className="me-2" />
                              Loading...
                            </td>
                          </tr>
                        ) : subCategories.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No subcategories found
                            </td>
                          </tr>
                        ) : (
                          subCategories.map((subCategory, index) => (
                            <tr key={subCategory.id}>
                              <td>{index + 1}</td>
                              <td>{subCategory.catagory_name}</td>
                              <td>{subCategory.subcatagory_name}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => startEditSubCategory(subCategory)}
                                    disabled={loading || editingSubCategoryId === subCategory.id}
                                  >
                                    <BsPencilSquare />
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => showDeleteConfirmation(subCategory.id)}
                                    disabled={loading || editingSubCategoryId === subCategory.id}
                                  >
                                    <BsTrash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this subcategory? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? (
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CashSubCatagory;
